import { AddonsType, AutomationsType, IntegrationsType } from '../../types/addons'
import { SubscriptionPlanEnum } from '../../types/company'
import { IStripeTaxes, IUpcomingInvoicePrices } from '../../types/stripe'
import { addons, automations } from '../product-addons'
import { IPrice, IProduct, PriceName, ProductName, getProducts } from './prices'
import { getStripeTaxes } from './stripe-taxes'
import { getStateName } from './get-state-name'

export class ProductService {
  private products: IProduct[]
  private taxRates: IStripeTaxes

  public constructor(private env: string) {
    this.products = getProducts(env !== 'prod')
    this.taxRates = getStripeTaxes(env)
  }

  public isProdEnvironment(): boolean {
    return this.env === 'prod'
  }

  public getAllProducts(): IProduct[] {
    return this.products
  }

  public getProductByName(name: ProductName): IProduct {
    return this.products.find((product) => product.name === name) as IProduct
  }

  public getAllPrices(): IPrice[] {
    return this.products.reduce((prices: IPrice[], currentValue) => {
      const pricesArray: IPrice[] = currentValue.prices
      return prices.concat(pricesArray)
    }, [])
  }

  /**
   * @returns All prices for main products(CORE_MONTHLY, CORE_ANNUAL, COMPLETE_MONTHLY, COMPLETE_ANNUAL)
   */
  public getAllPricesForMainProducts(): IPrice[] {
    return this.products.flatMap((product) => (product.isMainProduct ? product.prices : []))
  }

  public getProductByPriceId(priceId: string): IProduct {
    return this.products.find((product) => product.prices.some((price) => price.id === priceId)) as IProduct
  }

  /**
   * @returns All prices for additional products(ADDONS, AUTOMATIONS, INTEGRATIONS)
   */
  public getAllPricesForAdditionalProducts(): IPrice[] {
    return this.products.flatMap((product) => (!product.isMainProduct ? product.prices : []))
  }

  public getPriceById(priceId: string): IPrice {
    return this.getAllPrices().find((price) => price.id === priceId) as IPrice
  }

  public getPriceByName(name: PriceName): IPrice {
    return this.getAllPrices().find((price) => price.name === name) as IPrice
  }

  public getAllNewPrices2025(): IPrice[] {
    const names = [
      PriceName.CORE_MONTHLY_2025,
      PriceName.CORE_MONTHLY_2025_GF,
      PriceName.CORE_ANNUAL_2025,
      PriceName.CORE_ANNUAL_2025_GF,
      PriceName.COMPLETE_MONTHLY_2025,
      PriceName.COMPLETE_MONTHLY_2025_GF,
      PriceName.COMPLETE_ANNUAL_2025,
      PriceName.COMPLETE_ANNUAL_2025_GF,
      PriceName.FREEMIUM_2025,
    ]
    return this.getAllPrices().filter((price) => names.includes(price.name))
  }

  public getGrandfatheringPrices(): IPrice[] {
    const names = [PriceName.CORE_MONTHLY_2025_GF, PriceName.CORE_ANNUAL_2025_GF, PriceName.COMPLETE_MONTHLY_2025_GF, PriceName.COMPLETE_ANNUAL_2025_GF]
    return this.getAllPrices().filter((price) => names.includes(price.name))
  }

  // Prices currently offered to new clients at signup ( 2$ and 4$)
  public getCurrentPrices(): IPrice[] {
    const names = [PriceName.CORE_MONTHLY_2025, PriceName.CORE_ANNUAL_2025, PriceName.COMPLETE_MONTHLY_2025, PriceName.COMPLETE_ANNUAL_2025]
    return this.getAllPrices().filter((price) => names.includes(price.name))
  }

  public getCurrentPricesPerPlan(): IUpcomingInvoicePrices {
    const currentPrices = this.getCurrentPrices()
    return {
      Core: {
        monthly: currentPrices.find((price) => price.plan === SubscriptionPlanEnum.core && price.period === 'monthly') as IPrice,
        annual: currentPrices.find((price) => price.plan === SubscriptionPlanEnum.core && price.period === 'annual') as IPrice,
      },
      Complete: {
        monthly: currentPrices.find((price) => price.plan === SubscriptionPlanEnum.complete && price.period === 'monthly') as IPrice,
        annual: currentPrices.find((price) => price.plan === SubscriptionPlanEnum.complete && price.period === 'annual') as IPrice,
      },
    }
  }

  public getTaxes(country?: string, state?: string | null): string[] {
    const canadaCountryCode = 'CA'
    if (country && country.toUpperCase() === canadaCountryCode && state) {
      return this.taxRates[getStateName(state)]
    }
    return this.taxRates.noTax
  }

  public getAllTaxes(): IStripeTaxes {
    return this.taxRates
  }

  public getPriceUnitByProductName(name: ProductName): number {
    const addonsProduct = this.getProductByName(name)
    const addonsPrice = this.getPriceById(addonsProduct?.id as string)

    return addonsPrice?.tiers[0]?.unit_amount ? addonsPrice?.tiers[0]?.unit_amount / 100 : 0.5
  }

  public getProductByAddonType(type: AddonsType | AutomationsType | IntegrationsType) {
    if (addons.includes(type as AddonsType)) {
      return this.getProductByName(ProductName.ADDONS)
    }
    if (automations.includes(type as AutomationsType)) {
      return this.getProductByName(ProductName.AUTOMATIONS)
    }
    //todo add integration
  }

  public getFreemiumPriceId(): string {
    return this.getPriceByName(PriceName.FREEMIUM_2025).id
  }

  public areQuantitiesInSameTier(priceId: string, quantity1: number, quantity2: number): boolean {
    const price = this.getPriceById(priceId)
    if (!price || !price.tiers || price.tiers.length === 0) {
      return false
    }

    const getTierIndex = (quantity: number): number => {
      for (let i = 0; i < price.tiers.length; i++) {
        const tier = price.tiers[i]
        if (tier.up_to === 'inf' || quantity <= tier.up_to) {
          return i
        }
      }
      return price.tiers.length - 1 // Fallback to last tier (inf)
    }

    return getTierIndex(quantity1) === getTierIndex(quantity2)
  }

  public static getAnnualTierInfo(price: IPrice, quantity: number): { tierMin: number; tierMax: number | 'inf' } | null {
    if (!price.tiers || price.tiers.length === 0) return null
    for (let i = 0; i < price.tiers.length; i++) {
      const tier = price.tiers[i]
      if (tier.up_to === 'inf' || quantity <= (tier.up_to as number)) {
        const tierMin = i === 0 ? 1 : (price.tiers[i - 1].up_to as number) + 1
        return { tierMin, tierMax: tier.up_to }
      }
    }
    return null
  }

  public getTierFlatAmount(priceId: string, quantity: number): number | undefined {
    const price = this.getPriceById(priceId)
    if (!price || !price.tiers || price.tiers.length === 0) {
      return undefined
    }
    for (const tier of price.tiers) {
      if (tier.up_to === 'inf' || quantity <= tier.up_to) {
        if (tier.flat_amount !== undefined && tier.flat_amount !== null) {
          return tier.flat_amount
        }
        if (tier.unit_amount !== undefined && tier.unit_amount !== null) {
          return tier.unit_amount * quantity
        }
        return undefined
      }
    }
    return undefined
  }
}
